import { useEffect, memo, useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { isEmpty, isFunction, isNil } from 'lodash';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useGoToRoute from 'hooks/GoToRoute';
import useQueryParams from 'hooks/UseQueryParams';
//Import UI Components
import { Modal, Button } from '@geneui/components';
//Import Components
import AccessControl from 'components/common/AccessControl';
import CustomTabs from 'components/common/CustomTabs';
import CustomerJourneyGeneralDetails from 'components/common/CustomerJourneyGeneralDetails';
import CJVisualizationInfo from 'components/CustomerJourney/CJVisualizationInfo';
import ActivityTimeLine from '../CustomerJourney/ActivityTimeLine';
import CustomerJourneyActionsButtons from 'components/CustomerJourneyList/CustomerJourneyActionsButtons';
//Import Constants
import {
    DependencyObjectTypeLabels,
    l,
    RealtimePromotionTypes,
    CustomerJourneyInfoModalTitleLabels,
} from 'constants/common';
import { modalsStateSetDictKeys } from 'components/CustomerJourneyList/config';
import { RoutesList } from 'routes';
// Import Services
import { CustomerJourneyHttpService, UtilsHttpService } from 'services/http';
//Import SCSS
import 'assets/scss/customerJourneyInfoModal.scss';
//Import Version History
import {
    VersionHistoryIconWithPopover,
    VersionHistorySelectedVersionLabel,
    VersionHistoryProviderHOC,
    useVersionHistoryContext,
} from 'components/VersionHistory';
// Import Enums
import { PermissionEnum, ObjectTypeEnum } from 'enums';

const {
    REALTIME_JOURNEY_EDIT,
    REALTIME_JOURNEY_CLONE,
    REALTIME_JOURNEY_VIEW,
    REALTIME_JOURNEY_REPORT,
    REALTIME_JOURNEY_VISUALIZATION_CREATE,
    REALTIME_JOURNEY_VISUALIZATION_EDIT,
    REALTIME_ACTION_EDIT,
    REALTIME_ACTION_CLONE,
    REALTIME_ACTION_VIEW,
    REALTIME_ACTION_REPORT,
} = RoutesList;

interface Props {
    workflowId: number;
    isVisible: boolean;
    handleClose: () => void;
    promotionType: number;
    hasVisualization?: boolean;
    handleInfoModalActionClick?: (string, object) => void;
    predefinedKeyValue?: { regions: string; categories: string };
    withTitleActions?: boolean;
}

const CustomerJourneyInfoModal: FC<Props> = ({
    workflowId,
    handleClose,
    isVisible,
    hasVisualization = false,
    promotionType,
    handleInfoModalActionClick,
    withTitleActions = true,
    predefinedKeyValue,
}) => {
    const { t } = useTranslation();
    const goToRouteQueryParams = useQueryParams();
    const { search } = useLocation();

    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingUsedIn, setIsLoadingUsedIn] = useState(false);
    const [generalInfoData, setGeneralInfoData] = useState<AnyObject>({});
    const [usedIn, setUsedIn] = useState<Nullable<AnyObject[]>>(null);

    const { pointInTime, isHistoryMode } = useVersionHistoryContext();

    const { getRoutePath, goToPath } = useGoToRoute();
    const { doGetRequest } = useRequest();

    const { getCustomerJourneyGeneralDetailsRequest, getObjectDependencies } = useCancelableRequests(
        {
            getCustomerJourneyGeneralDetailsRequest: CustomerJourneyHttpService.getCustomerJourneyGeneralDetails,
            getObjectDependencies: UtilsHttpService.getObjectDependencies,
        },
        { takeLatest: ['getCustomerJourneyGeneralDetailsRequest', 'getObjectDependencies'] },
    );

    const getCustomerJourneyGeneralDetails = () => {
        setIsLoading(true);
        doGetRequest(getCustomerJourneyGeneralDetailsRequest.request, {
            queryString: { workflowId, pointInTime },
            successCallback: (Data) => {
                setGeneralInfoData(Data ?? {});
            },
        }).then(({ IsCanceled }) => {
            // A superseded call must not hide the loader of the call that replaced it
            if (!IsCanceled) {
                setIsLoading(false);
            }
        });
    };

    const getCustomerJourneyUsedIn = () => {
        setIsLoadingUsedIn(true);
        doGetRequest(getObjectDependencies.request, {
            queryString: { objectId: workflowId, dependencyType: DependencyObjectTypeLabels.Workflow },
            successCallback: (Data) => {
                setUsedIn(Data);
            },
        }).then(({ IsCanceled }) => {
            // A superseded call must not hide the loader of the call that replaced it
            if (!IsCanceled) {
                setIsLoadingUsedIn(false);
            }
        });
    };

    const init = () => {
        getCustomerJourneyGeneralDetails();
        getCustomerJourneyUsedIn();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, [pointInTime]);

    const tabsConfig = [
        {
            tabTitle: t(l.GeneralInfo),
            tabKey: '0',
            tabContent: (
                <CustomerJourneyGeneralDetails
                    workflowId={workflowId}
                    isLoading={isLoading}
                    title={generalInfoData.Name}
                    description={generalInfoData.Description}
                    {...(!isNil(predefinedKeyValue) && {
                        predefinedRegions: predefinedKeyValue.regions,
                    })}
                    {...(!isNil(predefinedKeyValue) && {
                        predefinedCategories: predefinedKeyValue.categories,
                    })}
                    startDate={generalInfoData.Schedule?.StartDate}
                    endDate={generalInfoData.Schedule?.EndDate}
                    mode={generalInfoData.State}
                    status={generalInfoData.Status}
                    User={generalInfoData.CreatorName}
                    createdDate={generalInfoData.CreatedDate}
                    lastModified={generalInfoData.ModifiedDate}
                    lastModifier={generalInfoData.ModifierName}
                    hasBudget={generalInfoData.HasBudgets}
                    customFields={generalInfoData.CustomFields}
                    usedIn={usedIn}
                    isLoadingUsedIn={isLoadingUsedIn}
                />
            ),
        },
        {
            tabTitle: t(l.Timeline),
            tabKey: '1',
            tabContent: <ActivityTimeLine workflowId={workflowId} />,
        },
        ...(hasVisualization
            ? [
                  {
                      tabTitle: t(l.Visualization),
                      tabKey: '2',
                      tabContent: <CJVisualizationInfo workflowId={workflowId} />,
                  },
              ]
            : []),
    ];

    // Actions Part

    const deleteCustomerJourneyAction = (customerJourney) => {
        handleInfoModalActionClick?.(modalsStateSetDictKeys.deleteCustomerJourney, customerJourney);
    };

    const archiveCustomerJourneyAction = (customerJourney) => {
        handleInfoModalActionClick?.(modalsStateSetDictKeys.archiveCustomerJourney, customerJourney);
    };

    const unArchiveCustomerJourneyAction = (customerJourney) => {
        handleInfoModalActionClick?.(modalsStateSetDictKeys.unarchiveCustomerJourney, customerJourney);
    };

    const stopSendCustomerJourneyAction = (customerJourney) => {
        handleInfoModalActionClick?.(modalsStateSetDictKeys.stopSendCustomerJourney, customerJourney);
    };

    const startCustomerJourneyAction = (customerJourney) => {
        handleInfoModalActionClick?.(modalsStateSetDictKeys.startCustomerJourney, customerJourney);
    };

    const resumeCustomerJourneyAction = (workflow) => {
        handleInfoModalActionClick?.(modalsStateSetDictKeys.resumeCustomerJourney, workflow);
    };

    const getEditCustomerJourneyPath = (workflow) => {
        const { WorkflowId, PromotionType } = workflow;
        const params = {
            workflowId: WorkflowId,
        };
        const route =
            PromotionType === +RealtimePromotionTypes.ActionCampaign ? REALTIME_ACTION_EDIT : REALTIME_JOURNEY_EDIT;
        return getRoutePath(route, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const goToEditCustomerJourney = (workflow) => {
        const path = getEditCustomerJourneyPath(workflow);
        goToPath(path);
    };

    const getCloneCustomerJourneyPath = (workflow) => {
        const { WorkflowId, PromotionType } = workflow;
        const params = {
            workflowId: WorkflowId,
        };
        const route =
            PromotionType === +RealtimePromotionTypes.ActionCampaign ? REALTIME_ACTION_CLONE : REALTIME_JOURNEY_CLONE;

        return getRoutePath(route, params);
    };

    const editCustomerJourneyAction = (workflow) => {
        const { WorkflowId } = workflow;

        doGetRequest(getObjectDependencies.request, {
            queryString: { objectId: WorkflowId, dependencyType: DependencyObjectTypeLabels.Workflow },
            successCallback: (Data) => {
                if (isEmpty(Data)) {
                    goToEditCustomerJourney(workflow);
                    handleClose();
                } else {
                    handleInfoModalActionClick?.(modalsStateSetDictKeys.editCustomerJourney, {
                        objects: Data,
                        workflow,
                    });
                }
            },
        });
    };

    const getViewCustomerJourneyPath = (workflow) => {
        const { WorkflowId, PromotionType } = workflow;
        const params = { workflowId: WorkflowId };
        const route =
            PromotionType === +RealtimePromotionTypes.ActionCampaign ? REALTIME_ACTION_VIEW : REALTIME_JOURNEY_VIEW;
        return getRoutePath(route, params);
    };

    const getReportCustomerJourneyPath = (workflow) => {
        const { WorkflowId, PromotionType } = workflow;
        const params = { workflowId: WorkflowId };
        const route =
            PromotionType === +RealtimePromotionTypes.ActionCampaign ? REALTIME_ACTION_REPORT : REALTIME_JOURNEY_REPORT;
        return getRoutePath(route, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const getCustomerJourneyVisualizationPath = (workflow) => {
        const { WorkflowId, HasVisualization } = workflow;
        const params = { workflowId: WorkflowId };
        const path = HasVisualization ? REALTIME_JOURNEY_VISUALIZATION_EDIT : REALTIME_JOURNEY_VISUALIZATION_CREATE;

        return getRoutePath(path, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    return (
        <Modal
            className="crm-customer-journey-info-modal"
            background="dark-background"
            position="center"
            title={
                <div className="crm-object-list-modal-head">
                    <span className="crm-object-list-modal-h-text">
                        {t(l[CustomerJourneyInfoModalTitleLabels[promotionType]])}
                    </span>
                    {withTitleActions && (
                        <div className="crm-object-list-modal-h-icons">
                            <VersionHistorySelectedVersionLabel
                                objectId={workflowId}
                                objectTypeId={ObjectTypeEnum.Workflow}
                            />
                            <VersionHistoryIconWithPopover
                                objectId={workflowId}
                                objectTypeId={ObjectTypeEnum.Workflow}
                            />
                            {!isHistoryMode && isFunction(handleInfoModalActionClick) && (
                                <AccessControl
                                    mandatoryPermissions={[PermissionEnum.CreateWorkflow, PermissionEnum.ViewWorkflow]}
                                >
                                    <CustomerJourneyActionsButtons
                                        data={generalInfoData}
                                        unArchiveCustomerJourneyAction={unArchiveCustomerJourneyAction}
                                        archiveCustomerJourneyAction={archiveCustomerJourneyAction}
                                        deleteCustomerJourneyAction={deleteCustomerJourneyAction}
                                        editCustomerJourneyAction={editCustomerJourneyAction}
                                        getEditCustomerJourneyPath={getEditCustomerJourneyPath}
                                        getCloneCustomerJourneyPath={getCloneCustomerJourneyPath}
                                        stopSendCustomerJourneyAction={stopSendCustomerJourneyAction}
                                        startCustomerJourneyAction={startCustomerJourneyAction}
                                        getViewCustomerJourneyPath={getViewCustomerJourneyPath}
                                        resumeCustomerJourneyAction={resumeCustomerJourneyAction}
                                        getReportCustomerJourneyPath={getReportCustomerJourneyPath}
                                        getCreateCustomerJourneyVisualizationPath={getCustomerJourneyVisualizationPath}
                                        getEditCustomerJourneyVisualizationPath={getCustomerJourneyVisualizationPath}
                                        isInfoVisible={false}
                                        handleCloseModal={handleClose}
                                    />
                                </AccessControl>
                            )}
                        </div>
                    )}
                </div>
            }
            closable={false}
            onCancel={handleClose}
            closeOnClickOutside={true}
            visible={isVisible}
            size="content-size"
            footer={
                <Button appearance="minimal" onClick={handleClose}>
                    {t(l.Close)}
                </Button>
            }
        >
            <CustomTabs tabsConfig={tabsConfig} />
        </Modal>
    );
};

export default memo(VersionHistoryProviderHOC(CustomerJourneyInfoModal));
